import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  ADT_EXTENSION_STORE_URL,
} from "@/lib/adt-extension";

import type { CcRunState } from "@/api/code-companion";
import { useAdtLogonFlow } from "@/hooks/useAdtLogonFlow";
import { useAdtStatus } from "@/hooks/useApi";
import {
  SESSION_ALERT_MIN,
  elapsedMs,
  formatCost,
  formatDuration,
  minutesUntil,
  sessionUrgency,
} from "@/lib/code-companion-session";
import { CC_STRIP_BG } from "@/lib/cc-trace-tokens";

/**
 * A faixa do maestro, sob o hero (S-064 ondas 2 e 7).
 *
 * O run escreve ABAP no sistema do cliente usando a **sessão ADT de quem clicou**, e
 * ela dura ~30 min. Quando cai, o run pausa: recuperável, mas no meio de uma
 * apresentação é a diferença entre uma pausa explicada e uma tela que parou.
 *
 * O dado já existia inteiro desde a S-048; o defeito era de **lugar**, e agora de
 * **forma**. A onda 2 a pôs no board; a onda 7 a alinha ao protótipo: faixa navy
 * contínua com o hero, monospace, uma linha só, lida da esquerda para a direita como
 * quem conduz · o que está acontecendo · quanto resta.
 *
 * **O relógio anda sozinho.** O poll do status é de 60s, mas o decorrido e os minutos
 * restantes são recalculados a cada segundo a partir das marcas que o servidor mandou.
 * Número que só muda a cada minuto parece congelado, que é exatamente a dúvida que a
 * faixa existe para tirar.
 *
 * **Reconectar é oferecido antes de precisar.** No alerta o botão aparece, sem esperar
 * o run pausar: reconectar leva ~20s e a sessão morre no meio do trabalho, não no fim.
 */

/** Cor do texto da sessão por urgência, sobre o navy da faixa. */
const TONE: Record<string, string> = {
  ok: "#fff",
  warn: "var(--exed-gold)",
  alert: "#ffb4b8",
  gone: "#ffb4b8",
};

/** Um relógio que bate de segundo em segundo, para o decorrido não parecer travado. */
function useTick(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [active]);
  return now;
}

interface Props {
  projectId: string;
  /** O run vivo do pacote, ou `null` quando não há nenhum. */
  run?: CcRunState | null;
}

export default function ConductorStrip({ projectId, run }: Props) {
  const { t } = useTranslation();
  const { data: session, isLoading: loadingSession } = useAdtStatus(projectId);

  const running =
    !!run && (run.status === "running" || run.status === "queued");
  const now = useTick(running || !!session);

  const mins = minutesUntil(session?.expires_at, now);
  const urgency = sessionUrgency(session?.status, mins);
  const elapsed = formatDuration(
    elapsedMs(run?.started_at, run?.finished_at, now),
  );

  // Quem entra por aqui dispara o mesmo logon do card, e sem a extensão cai num
  // loopback morto com o ticket queimado, sem pista da causa. Detecção one-shot: a faixa
  // não é tela de onboarding, então não fica re-checando no foco como o card faz.
  //
  // 🔶 **Só enquanto o logon está sendo oferecido.** O aviso serve a quem está prestes a
  // clicar em reconectar; ao lado de uma sessão viva ele viraria ruído permanente para
  // quem usa navegador onde a sonda não responde. Mesma condição do botão, de propósito.
  const offersLogon =
    !loadingSession && (urgency === "gone" || urgency === "alert");
  // #593: a sondagem e o par pedir-e-abrir saem daqui para o hook. A política `once`
  // é declarada na chamada, e não mais defendida só por comentário: a faixa não é tela
  // de onboarding, então não re-checa no foco como o card faz.
  const adtLogon = useAdtLogonFlow(projectId, { enabled: offersLogon, watch: "once" });

  const sep = <span style={{ color: "rgba(255,255,255,.25)" }}>·</span>;

  return (
    <div
      data-testid="conductor-strip"
      className="flex flex-wrap items-center gap-x-3.5 gap-y-1 px-6 py-2"
      style={{
        background: CC_STRIP_BG,
        color: "rgba(255,255,255,.7)",
        fontFamily: "var(--font-mono)",
        fontSize: "10.5px",
        letterSpacing: ".05em",
        borderBottom: "1px solid rgba(255,255,255,.07)",
      }}
    >
      {/* quem conduz: a identidade SAP que assina a change document no alvo */}
      <span
        data-testid="conductor-who"
        className="flex items-center gap-1.5"
        style={{ color: "var(--exed-gold)" }}
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        >
          <path d="m4 17 6-6-6-6M12 19h8" />
        </svg>
        {/* Enquanto não se leu, não se afirma: "ninguém conectado" durante o primeiro
            render (ou com o /adt-status falhando) é o mesmo texto de sessão caída. */}
        {session?.sap_responsible ||
          (loadingSession ? t("reading session…") : t("no one connected"))}
      </span>

      {run && (
        <>
          {sep}
          <span data-testid="run-state" className="flex items-center gap-2">
            <span>{run.status}</span>
            {elapsed && <span style={{ color: "#fff" }}>{elapsed}</span>}
            {run.total_objects > 0 && (
              <span>
                {t("{{n}}/{{total}} objects", {
                  n: run.concluded_objects,
                  total: run.total_objects,
                })}
              </span>
            )}
            {run.actual_cost_usd > 0 && (
              <span data-testid="run-cost">
                {formatCost(run.actual_cost_usd)}
              </span>
            )}
          </span>
        </>
      )}

      {sep}
      <span data-testid="session-state">
        {t("ADT session")}{" "}
        <strong style={{ color: TONE[urgency], fontWeight: 500 }}>
          {loadingSession
            ? t("…")
            : urgency === "gone"
              ? t("down")
              : mins === null
                ? t("live")
                : t("{{mins}} min left", { mins })}
        </strong>
      </span>

      <div className="flex-1" />

      {/* O aviso mora na própria faixa: um segundo lugar para o mesmo alerta é um
          lugar a mais para ele ficar desatualizado. */}
      {urgency === "warn" && (
        <span data-testid="strip-warning" style={{ color: "var(--exed-gold)" }}>
          {t("finish what is running before starting a new package")}
        </span>
      )}
      {urgency === "alert" && (
        <span data-testid="strip-alert" style={{ color: TONE.alert }}>
          {t("under {{mins}} min: reconnect before starting anything", {
            mins: SESSION_ALERT_MIN,
          })}
        </span>
      )}

      {/* Oferecido no alerta, não só depois de cair. */}
      {offersLogon && (
        <button
          type="button"
          data-testid="strip-reconnect"
          onClick={adtLogon.start}
          disabled={adtLogon.isPending}
          className="rounded px-2.5"
          style={{
            height: "24px",
            background: "rgba(255,255,255,.08)",
            border: "1px solid rgba(255,255,255,.16)",
            color: "#fff",
            fontFamily: "var(--font-mono)",
            fontSize: "10px",
            letterSpacing: ".06em",
          }}
        >
          {t("Reconnect ADT")}
        </button>
      )}
      {/* #593, achado na review: a recusa do logon precisa aparecer aqui também, senão
          o botão morre calado. Cabe na faixa porque é uma linha curta. */}
      {offersLogon && adtLogon.error !== null && (
        <span
          data-testid="strip-logon-error"
          style={{ color: TONE.alert, fontFamily: "var(--font-mono)", fontSize: "10px" }}
        >
          {adtLogon.error || t("Could not start the ADT logon.")}
        </span>
      )}
      {offersLogon && adtLogon.needsExtension && adtLogon.extension === null && (
        <a
          data-testid="strip-ext-instalar"
          href={ADT_EXTENSION_STORE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="rounded px-2.5"
          style={{
            height: "24px",
            lineHeight: "24px",
            color: "rgba(255,255,255,.75)",
            fontFamily: "var(--font-mono)",
            fontSize: "10px",
            textDecoration: "underline",
          }}
        >
          {t("Install the extension")}
        </a>
      )}
    </div>
  );
}
